import {AppInitializeService} from './app-initialize-service';
import {inject, Injectable} from './di';
import {TvModuleName} from './modules/tv-module-name';
import {ConfigService} from './config/config-service';
import {Logger} from './logger';
import {TvModuleService} from './modules/tv-module-service';
import type {TvWidgetModule} from './modules/tv-widget-module';
import type {WidgetMenuItem, WidgetMenuItemType} from './struct/structure';

@Injectable()
export class WidgetService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);
  private readonly tvModuleService = inject(TvModuleService);

  /**
   * List of initialized widgets.
   * Key - widget MenuItem id.
   * Value - widget information object.
   */
  private readonly initializedWidgets: Record<WidgetMenuItem['id'], WidgetInfo> = {};

  private readonly widgets: Record<WidgetMenuItemType, WidgetConfig> = {
    weather: {
      updateInterval: 30 * 60 * 1000, // 30 mins
      name: TvModuleName.Weather,
    },
    gorkiGorodData: {
      updateInterval: 10 * 60 * 1000, // 10 mins
      name: TvModuleName.GorkiGorod,
    },
    airportSchedule: {
      name: TvModuleName.Flights,
    },
  };

  constructor() {
    window.widgetObserver = this;
  }

  public init(): Promise<boolean> | boolean {
    this.widgets.weather.updateInterval = this.configService.config.widgets?.weather?.timeoutUpdate || 30 * 60 * 1000;
    return true;
  }

  /**
   * Initialize widget module
   * @param widgetMenuItemId
   * @param widgetMenuItem
   */
  public async add(widgetMenuItemId: WidgetMenuItem['id'], widgetMenuItem: WidgetMenuItem) {
    try {
      const widgetType = widgetMenuItem.widget?.type;
      if (!widgetType) {
        this.logger.error(
          `[WidgetService.add] Unable to add widget with empty widget type. MenuItem id: "${widgetMenuItemId}"`,
          widgetMenuItem,
        );
        return;
      }
      const widget = this.widgets[widgetType];
      if (!widget) {
        const availableWidgets = Object.keys(this.widgets).join(', ');
        this.logger.error(
          `[WidgetService.add] Unknown widget type: ${widgetType}. Available types: ${availableWidgets}. Please check the content.`,
          widgetMenuItem,
        );
        return;
      }

      if (this.initializedWidgets[widgetMenuItemId]) {
        this.logger.warn(
          `[WidgetService.add] Widget for menu item "${widgetMenuItemId}" already initialized`,
          widgetMenuItem,
        );
        return;
      }

      this.logger.debug(`[WidgetService.add] Adding widget "${widget.name}"...`);

      let module: TvWidgetModule;
      try {
        module = (await this.tvModuleService.load(widget.name)) as TvWidgetModule;
      } catch (err) {
        this.logger.error(`[WidgetService] Unable to load Widget TV module "${widget.name}"`, err);
        return;
      }

      if (!module || typeof module.initWidget !== 'function') {
        this.logger.error(`[WidgetService] TV module "${widget.name}" should implement TvWidgetModule interface!`);
        return;
      }

      try {
        await module.initWidget(widgetMenuItemId, widgetMenuItem);
      } catch (err) {
        this.logger.error(`[WidgetService] Unable to initialize Widget TV module "${widget.name}"`, err);
        return;
      }

      const widgetInfo: WidgetInfo = {
        ...widget,
        menuItemId: widgetMenuItemId,
        type: widgetType,
        module: module,
        updateInterval: widget.updateInterval,
      };
      this.initializedWidgets[widgetMenuItemId] = widgetInfo;

      // The first data request is already made by initWidget — only schedule the periodic refresh here
      this.scheduleRefreshInterval(widgetMenuItemId, widgetInfo);
    } catch (err) {
      this.logger.error(
        `[WidgetService.add] Error adding widget for menu item "${widgetMenuItemId}"`,
        err,
        widgetMenuItem,
      );
    }
  }

  /**
   * Refresh all widgets data and schedule a refresh interval.
   */
  public refreshWidgets() {
    for (const widgetMenuItemId in this.initializedWidgets) {
      void this.refreshWidget(widgetMenuItemId);
    }
  }

  /**
   * Check if a widget with such MenuItem id exists and already initialized.
   * @param widgetMenuItemId Widget MenuItem id.
   */
  public hasInitializedWidget(widgetMenuItemId: WidgetMenuItem['id']): boolean {
    return Object.keys(this.initializedWidgets).includes(widgetMenuItemId);
  }

  /**
   * Refresh the widget for a selected menu item and schedule a refresh interval.
   * @param widgetMenuItemId
   */
  public async refreshWidget(widgetMenuItemId: WidgetMenuItem['id']) {
    const widgetCfg = this.initializedWidgets[widgetMenuItemId];

    if (!widgetCfg) {
      this.logger.warn(
        `[WidgetService] Unable to find initialized Widget TV module with menu item id: "${widgetMenuItemId}"`,
      );
      return false;
    }

    this.scheduleRefreshInterval(widgetMenuItemId, widgetCfg);

    await widgetCfg.module?.refresh(widgetMenuItemId);
  }

  /**
   * Schedule (or reschedule) the periodic data refresh for a widget.
   * Called right at widget registration, so the periodic refresh does not
   * depend on the timing of an external refreshWidgets() call.
   * Widgets without `updateInterval` are not refreshed periodically.
   * @param widgetMenuItemId
   * @param widgetCfg
   */
  private scheduleRefreshInterval(widgetMenuItemId: WidgetMenuItem['id'], widgetCfg: WidgetInfo): void {
    if (widgetCfg.interval != null) {
      clearInterval(widgetCfg.interval);
      widgetCfg.interval = undefined;
    }

    if (widgetCfg.updateInterval) {
      widgetCfg.interval = setInterval(() => {
        void widgetCfg.module?.refresh(widgetMenuItemId);
      }, widgetCfg.updateInterval);
    }
  }

  /**
   * Get the Widget TV Module instance by Widget Type and (optional) MenuItem id.
   * @param widgetType 'weather' | 'gorkiGorodData' | 'airportSchedule'
   * @param widgetMenuItemId
   */
  public get(widgetType: WidgetMenuItemType, widgetMenuItemId?: WidgetMenuItem['id']): TvWidgetModule | undefined {
    return Object.values(this.initializedWidgets).find(
      (widgetInfo) =>
        (widgetMenuItemId == null || widgetInfo.menuItemId === widgetMenuItemId) && widgetInfo.type === widgetType,
    )?.module;
  }

  public async open(widgetMenuItemId: WidgetMenuItem['id']) {
    const widgetModule = this.initializedWidgets[widgetMenuItemId]?.module;
    if (!widgetModule) {
      this.logger.warn(
        `[WidgetService.open] Unable to find initialized Widget TV module with menu item id: "${widgetMenuItemId}"`,
      );
      return;
    }
    await widgetModule.open();
  }

  /**
   * Call a function on a Widget TV Module instance by MenuItem id.
   * @param widgetMenuItemId Widget menu item ID
   * @param functionName name of the function on widget TV module
   * @param data optional function argument.
   */
  public eval(widgetMenuItemId: WidgetMenuItem['id'], functionName: keyof TvWidgetModule, data?: any) {
    const widgetInfo = this.initializedWidgets[widgetMenuItemId];
    const widgetModule = widgetInfo?.module;
    if (!widgetModule) {
      this.logger.warn(
        `[WidgetService.eval] Unable to find initialized Widget TV module with menu item id: "${widgetMenuItemId}"`,
      );
      return;
    }
    const func = widgetModule[functionName] as undefined | ((data: any) => void);
    if (!func || typeof func !== 'function') {
      this.logger.warn(
        `[WidgetService.eval] Unable to call "${functionName}" function on "${widgetInfo.name}" Widget TV module. Function was not found.`,
      );
      return;
    }
    try {
      void func.bind(widgetModule)(data);
    } catch (err) {
      this.logger.error(`[WidgetService.eval] "${widgetInfo.name}"."${functionName}" call throws exception`, err);
      return;
    }
  }

  /**
   * Destroy the widget module selected by MenuItem id.
   * This function cancels the refresh intervals and removes the module from the list of initialized widgets.
   * @param widgetMenuItemId
   * @private
   */
  public destroy(widgetMenuItemId: WidgetMenuItem['id']) {
    const widgetInfo = this.initializedWidgets[widgetMenuItemId];
    if (!widgetInfo) {
      this.logger.warn(
        `[WidgetService.destroy] Unable to find initialized Widget TV module with menu item id: "${widgetMenuItemId}"`,
      );
      return;
    }
    if (widgetInfo.interval != null) {
      clearInterval(widgetInfo.interval);
    }
    delete this.initializedWidgets[widgetMenuItemId];
  }
}

interface WidgetConfig {
  /**
   * TV widget module name.
   */
  name: TvModuleName;

  /**
   * Widget update interval in milliseconds.
   */
  updateInterval?: number;
}

interface WidgetInfo extends WidgetConfig {
  menuItemId: WidgetMenuItem['id'];

  /**
   * Update interval reference far calling `clearInterval`
   */
  interval?: NodeJS.Timeout | undefined;

  /**
   * TV widget module instance.
   */
  module: TvWidgetModule;

  /**
   * Widget type
   */
  type: WidgetMenuItem['widget']['type'];
}

declare global {
  interface Window {
    widgetObserver: WidgetService;
  }
  const widgetObserver: WidgetService;
}
